<template>
  <div>
    <embed-api-table
      title="Campaign Package Tier"
      module="Campaigns"
      v-bind:add-new-text="addNewText"
      @add-new="addNew"
      @click="campaignClicked"
      default-sort="startDate ASC"
      v-bind:columns="campaignColumns"
      v-bind:filter="campaignFilter"
      v-bind:refresh-for-id="lastRefreshTime">
    </embed-api-table>
    
    <package-data @added="onCampaignAdded" v-bind:view-obj="viewCampaignObj" v-bind:client-id="clientId"></package-data>
    <alert-dlg dlg-id="modal-campaign-alert" v-bind:title="alertDlgTitle" v-bind:messages="alertDlgMessages"></alert-dlg>
    <progress-dlg job-title="Export PDF" v-bind:stage="pdfExportStage" v-bind:percentage="pdfExportPercent" v-bind:pdf="savedPdf"></progress-dlg>
    <overlapping-screenshot-dlg v-bind:overlapping-screenshots="overlappingScreenshots"></overlapping-screenshot-dlg>
  </div>
</template>

<script>
  import EmbedApiTable from "./EmbedApiTable.vue";
  import PackageData from "./PackageData.vue";
  import AlertDlg from "./AlertDlg.vue";
  import ProgressDlg from "./ProgressDlg.vue";
  import OverlappingScreenshotDlg from "./OverlappingScreenshotDlg.vue";
  import WebLLCRESTApi from "../api/api";
  import moment from "moment";
  import $ from "jquery";
  
  export default {
    created() {
      // prepare api
      this.api = new WebLLCRESTApi("Campaigns", this.$router);
      this.apiExternalServices = new WebLLCRESTApi("External-Services", this.$router);
      this.apiSearchResults = new WebLLCRESTApi("Search-Results", this.$router);
    },
    
    props: {
      clientId: String,
      canSave: Boolean
    },
    
    data() {
      return {
        campaignColumns: this.getColumns(),
        
        addNewText: (this.canSave ? "Add New Campaign" : null),
        
        campaignFilter: { clientId: this.clientId },
        
        viewCampaignObj: { operation: "new" },
        
        pdfExportStage: "",
        pdfExportPercent: 0,
        savedPdf: null,
        setSentCampaign: null,
        
        lastRefreshTime: "0",
        overlappingScreenshots: [],
        alertDlgTitle: "",
        alertDlgMessages: []
      };
    },
    
    methods: {
      addNew() {
        this.viewCampaignObj = { operation: "new" };
        $('#modal-add-package-data').modal('toggle');
      },
      
      onCampaignAdded() {
        // change the last refresh date so the table refreshes
        this.lastRefreshTime = Date.now().toString();
      },
      
      async campaignClicked(data) {
        const campaigns = data.rows.map((row) => row.item);
        const campaign = data.item.item;
        
        if(data.col.text == "View Details") {
          const externalServices = await this.apiExternalServices.getData(null, {
            where: { campaignId: campaign.id }
          });
          
          this.viewCampaignObj = {
            id: campaign.id,
            name: campaign.name,
            startDate: campaign.startDate,
            hasEndDate: campaign.hasEndDate,
            endDate: campaign.endDate,
            data: campaign.data,
            externalServices: externalServices,
          };
          
          $('#modal-add-package-data').modal('toggle');
        }
        else if(data.col.text == "Regenerate Report") {
          if (confirm("Are you sure you want to regenerate the report?")) {
            this.regenerateReport(campaign);
          }
        }
        else if(data.col.text == "Retry Automation") {
          if (confirm("Are you sure you want to rerun automation?")) {
            this.retryAutomation(campaign);
          }
        }
        else if(data.col.text == "Preview PDF") {
          this.savedPdf = null;
          await new Promise((resolve) => {
            const handler = $("#modal-job-progress").on("shown.bs.modal", () => { $("#modal-job-progress").off("shown.bs.modal", "**"); resolve(); });
            $("#modal-job-progress").modal("show");
          });
          
          this.setExportProgress("Loading Campaign Data");
          
          const pdfRes = await this.api.remoteMethod(campaign.id + "/pdf");
          
          if(pdfRes.status == 200) {
            const pdfJson = await pdfRes.json();
            
            if(pdfJson.error == "Overlapping Screenshots") {
              this.overlappingScreenshots = pdfJson.overlappingScreenshots;
              
              $("#modal-job-progress").modal("hide");
              $("#modal-show-overlapping").modal("show");
            }
            else if(pdfJson.error == "Screenshots Without Campaign") {
              $("#modal-job-progress").modal("hide");
              
              this.alertDlgTitle = "Screenshots not within a campaign date";
              this.alertDlgMessages = pdfJson.noCampaignScreenshots.map((noCampaignScreenshot) => {
                return noCampaignScreenshot.keyword + " " + moment.utc(noCampaignScreenshot.createdAt, "YYYY-MM-DDTHH:mm:ss.SSSZ").format("MM/DD/YYYY") + " [Page " + noCampaignScreenshot.page + "]: doesn't match campaign date";
              });
              
              $("#modal-campaign-alert").modal("show");
            }
            else if(pdfJson.error == "Missing Keywords") {
              $("#modal-job-progress").modal("hide");
              
              this.alertDlgTitle = "Missing Keywords";
              this.alertDlgMessages = pdfJson.missingKeywords.map((keywordInfo) => {
                  return "Keyword \"" + keywordInfo.keyword + "\" is missing in month " + keywordInfo.monthNum;
                });
              
              $("#modal-campaign-alert").modal("show");
            }
            else if(pdfJson.error == "Keyword Counts Don't Match") {
              $("#modal-job-progress").modal("hide");
              
              this.alertDlgTitle = "Keyword Counts Don't Match";
              this.alertDlgMessages = ["Your campaign says you should have " + (pdfJson.tierData.packageKeywords.num ? pdfJson.tierData.packageKeywords.num : pdfJson.tierData.packageKeywords) + " but only " + pdfJson.tierData.keywords.length + " have been added."];
              
              $("#modal-campaign-alert").modal("show");
            }
            else {
              this.setExportProgress("Laying out PDF");
              
              // attach the pdf so we can view it
              const filename = (function() {
                const client = pdfJson.client;
                
                if(client.firstName && client.lastName) {
                  return ((client.companyName ? client.companyName + "-" : "") +
                    client.firstName + " " + client.lastName).replace(/[^A-Za-z0-9\-\s]+/g, "").toLowerCase() +
                    "-start-" + moment(campaign.startDate).format("YYYY-MM-DD") + "-saved-" + moment().format("YYYY-MM-DD");
                }
                else if(client.companyName) {
                  return client.companyName.replace(/^[A-Za-z0-9\s]/g, "").toLowerCase() +
                    "-start-" + moment(campaign.startDate).format("YYYY-MM-DD") + "-saved-" + moment().format("YYYY-MM-DD");
                }
                else {
                  return "campaign-start-" + moment(campaign.startDate).format("YYYY-MM-DD") + "-saved-" + moment().format("YYYY-MM-DD");
                }
              })();
              
              const pdfDataURI = "data:application/pdf;base64," + pdfJson.pdf;
              const pdfImageRes = await fetch(pdfDataURI);
              const pdfBlob = await pdfImageRes.blob();
              
              this.setExportProgress("PDF File ready to generate");
              
              this.savedPdf = {
                pdf: pdfBlob,
                filename: filename
              };
            }
          }
          else {
            $("#modal-job-progress").modal("hide");
            
            this.alertDlgTitle = "PDF Failed to Open";
            this.alertDlgMessages = [ "Status code " + pdfRes.status ];
            
            $("#modal-campaign-alert").modal("show");
          }
        }
        else {
          $("#modal-job-progress").modal("hide");
          
          this.alertDlgTitle = "PDF Failed to Open";
          this.alertDlgMessages = [ (await pdfRes.text()) ];
          
          $("#modal-campaign-alert").modal("show");
        }
      },
      
      setExportProgress(stage) {
        const stages = [
          "Loading Campaign Data",
          "Loading Screenshots",
          "Laying out PDF",
          "PDF File ready to generate"
        ];
        
        this.pdfExportStage = stage;
        this.pdfExportPercent = Math.round((stages.indexOf(stage) * 100) / (stages.length - 1));
      },
      
      async regenerateReport(campaign) {
        // rehiglight all links for the current month of this campaign
        await this.api.remoteMethod(campaign.id + "/rehighlight");
        window.location.reload();
      },
      
      async retryAutomation(campaign) {
        // delete any failedapi screenshots
        const searchResults = await this.apiSearchResults.getData(null, { where: {
            status: "failedapi",
            clientId: campaign.clientId
          }
        });
        
        for(const searchResult of searchResults) {
          await this.apiSearchResults.deleteData(searchResult.id);
        }
        
        // request the automation for the month be rerun
        await this.api.remoteMethod(campaign.id + "/forcescreenshots");
      },
      
      getColumns() {
        const fnLoadInProgress = (item) => {
          const today = moment();
          const campaignStart = moment(item.startDate.split("T")[0]);
          const campaignEnd = (item.hasEndDate ? moment(item.endDate.split("T")[0] + "T23:59:59") : today);
          
          return today.isBetween(campaignStart, campaignEnd, undefined, '[]');
        };
        
        const cols = [
          { key: "name", "text": "Name", type: "String", sort: true },
          { key: "startDate", "text": "Start Date", type: "Date", sort: true },
          { key: "endDate", "text": "End Date", type: "Date", sort: true },
          { key: "inProgress", "text": "In Progress?", type: "Boolean", sort: false, fnAsyncLoad: fnLoadInProgress },
          { key: "savepdf", "text": "Preview PDF", type: "Event", sort: false },
          { key: "view", "text": "View Details", type: "Event", sort: false }
        ];
        
        if(this.canSave) {
          cols.splice(6, 0, { key: "regen", "text": "Regenerate Report", type: "Event", sort: false });
          cols.splice(7, 0, { key: "retryauto", "text": "Retry Automation", type: "Event", sort: false });
          cols.push({ key: "delete", "text": "Delete", type: "Delete", sort: false });
        }
        
        return cols;
      }
    },
    
    watch: {
      canSave(to, from) {
        this.addNewText = (this.canSave ? "Add New Campaign" : null);
        this.campaignColumns = this.getColumns();
      }
    },

    components: {
      EmbedApiTable,
      PackageData,
      AlertDlg,
      ProgressDlg,
      OverlappingScreenshotDlg
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/CampaignTable.vue